import React, { useState, useEffect, useRef } from 'react';

// Range slider duplo (min/max) sem dependência. Dois inputs `range` sobrepostos
// + barra visual. Usa onChange para feedback imediato e onCommit (mouseup/
// touchend/blur) pra disparar callback de filtro com debounce natural.
const RangeSlider = ({
  min = 0,
  max = 1000,
  step = 1,
  value,
  onChange,
  onCommit,
  format = (v) => v,
}) => {
  const [internal, setInternal] = useState(value || [min, max]);
  const minRef = useRef(null);
  const maxRef = useRef(null);

  useEffect(() => {
    if (value && (value[0] !== internal[0] || value[1] !== internal[1])) {
      setInternal(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value && value[0], value && value[1]]);

  const handleMin = (e) => {
    const v = Math.min(Number(e.target.value), internal[1] - step);
    const next = [v, internal[1]];
    setInternal(next);
    onChange?.(next);
  };
  const handleMax = (e) => {
    const v = Math.max(Number(e.target.value), internal[0] + step);
    const next = [internal[0], v];
    setInternal(next);
    onChange?.(next);
  };

  const commit = () => onCommit?.(internal);

  const range = max - min || 1;
  // Clamp em [0, 100] para impedir que valores fora do range estourem o
  // track e empurrem o layout (bug visual: ao mover max pra baixo e voltar,
  // a barra preenchida ocupava a tela inteira porque `right` virava negativo).
  const left = Math.max(0, Math.min(100, ((internal[0] - min) / range) * 100));
  const right = Math.max(0, Math.min(100, 100 - ((internal[1] - min) / range) * 100));

  return (
    <div className="w-full">
      <div className="relative h-6 overflow-hidden">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 bg-app-border rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 bg-primary rounded-full"
          style={{ left: `${left}%`, right: `${right}%` }}
        />
        <input
          ref={minRef}
          type="range"
          min={min}
          max={max}
          step={step}
          value={internal[0]}
          onChange={handleMin}
          onMouseUp={commit}
          onTouchEnd={commit}
          onBlur={commit}
          className="absolute top-0 left-0 w-full h-6 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:cursor-pointer"
          aria-label="Preço mínimo"
        />
        <input
          ref={maxRef}
          type="range"
          min={min}
          max={max}
          step={step}
          value={internal[1]}
          onChange={handleMax}
          onMouseUp={commit}
          onTouchEnd={commit}
          onBlur={commit}
          className="absolute top-0 left-0 w-full h-6 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:cursor-pointer"
          aria-label="Preço máximo"
        />
      </div>
      <div className="flex items-center justify-between mt-2 text-xs text-app-text-secondary">
        <span>{format(internal[0])}</span>
        <span>{format(internal[1])}</span>
      </div>
    </div>
  );
};

export default RangeSlider;
